// src/components/WebinarWaitlistForm.ts
//
// Mini-formulaire de capture d'email affiché sur webinaires.html dans le bandeau
// "Pas de webinaire prévu" (#webinar-empty), révélé par WebinarPageAnimator quand
// aucun webinaire n'est à venir. Soumet à `${VITE_API_URL}/api/webinar/waitlist`
// qui inscrit le contact à la liste Brevo dédiée "Liste d'attente webinaires".
//
// Sécurité : validation reCAPTCHA v2 via une modale légère (même UX que la
// newsletter), token vérifié côté serveur.

import { getAttributionForForms } from '../utils/attribution';
import { loadRecaptchaScript, loadRecaptchaOnInteraction } from '../utils/recaptcha';

const API_URL = import.meta.env.VITE_API_URL || 'https://forms.cryptoaccounting.fr';
const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

class WebinarWaitlistForm {
  private form!: HTMLFormElement;
  private emailInput!: HTMLInputElement;
  private submitBtn!: HTMLButtonElement;
  private feedbackEl!: HTMLElement | null;
  private isSubmitting = false;
  private static instance: WebinarWaitlistForm | null = null;

  constructor(form: HTMLFormElement) {
    if (WebinarWaitlistForm.instance) return WebinarWaitlistForm.instance;

    this.form = form;
    this.emailInput = form.querySelector('input[type="email"]') as HTMLInputElement;
    this.submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement;
    this.feedbackEl = document.getElementById('waitlist-feedback');

    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.onSubmit();
    });

    // reCAPTCHA chargé à la première interaction avec le formulaire, pas à l'ouverture de la page
    loadRecaptchaOnInteraction(this.form);

    WebinarWaitlistForm.instance = this;
  }

  private async onSubmit(): Promise<void> {
    if (this.isSubmitting) return;

    const email = (this.emailInput.value || '').trim();
    if (!EMAIL_REGEX.test(email)) {
      this.showFeedback('Veuillez entrer une adresse email valide.', 'error');
      this.emailInput.focus();
      return;
    }
    this.resetFeedback();

    await loadRecaptchaScript();
    this.openRecaptchaModal(email);
  }

  private openRecaptchaModal(email: string): void {
    const overlay = document.createElement('div');
    overlay.className = 'fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4';

    const modal = document.createElement('div');
    modal.className = 'bg-white rounded-2xl p-6 shadow-xl max-w-md w-full relative';
    modal.innerHTML = `
      <button type="button" data-action="close" class="absolute top-3 right-3 text-gray-400 hover:text-gray-600" aria-label="Fermer">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <div class="text-center mb-4">
        <h3 class="text-lg font-semibold text-primary-900">Confirmez que vous n'êtes pas un robot</h3>
        <p class="text-gray-600 text-sm mt-1">Une dernière étape pour vous prévenir du prochain webinaire.</p>
      </div>
      <div class="flex justify-center my-4" data-el="recaptcha"></div>
      <p class="hidden text-red-500 text-sm text-center mt-2" data-el="captcha-error">Veuillez valider le reCAPTCHA.</p>
      <button type="button" data-action="confirm" class="btn-primary w-full mt-2">Valider et m'inscrire</button>
    `;
    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    const captchaError = modal.querySelector('[data-el="captcha-error"]') as HTMLElement;
    const recaptchaContainer = modal.querySelector('[data-el="recaptcha"]') as HTMLElement;

    let widgetId: number | null = null;
    try {
      widgetId = window.grecaptcha.render(recaptchaContainer, {
        sitekey: RECAPTCHA_SITE_KEY,
        callback: () => captchaError.classList.add('hidden'),
      });
    } catch (err) {
      console.error('Erreur rendu reCAPTCHA (waitlist):', err);
    }

    const close = () => {
      if (overlay.parentNode) document.body.removeChild(overlay);
    };

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) close();
    });
    (modal.querySelector('[data-action="close"]') as HTMLButtonElement).addEventListener('click', close);

    (modal.querySelector('[data-action="confirm"]') as HTMLButtonElement).addEventListener('click', () => {
      let token = '';
      try {
        token = widgetId !== null && window.grecaptcha ? window.grecaptcha.getResponse(widgetId) : '';
      } catch {
        token = '';
      }
      if (!token) {
        captchaError.classList.remove('hidden');
        return;
      }
      close();
      this.submit(email, token);
    });
  }

  private async submit(email: string, recaptchaToken: string): Promise<void> {
    this.isSubmitting = true;
    this.setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/webinar/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          source: 'Webinar Waitlist',
          recaptchaToken,
          ...getAttributionForForms(),
        }),
      });

      const json = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(json?.error || "Une erreur est survenue. Réessayez.");
      }

      this.form.reset();
      this.showFeedback(
        json?.message || 'C\'est noté ! Nous vous préviendrons dès la prochaine session.',
        'success'
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Une erreur est survenue. Réessayez.';
      this.showFeedback(msg, 'error');
    } finally {
      this.isSubmitting = false;
      this.setLoading(false);
    }
  }

  private setLoading(loading: boolean): void {
    this.submitBtn.disabled = loading;
    this.submitBtn.classList.toggle('is-loading', loading);
  }

  private showFeedback(message: string, kind: 'success' | 'error'): void {
    if (!this.feedbackEl) return;
    this.feedbackEl.textContent = message;
    this.feedbackEl.hidden = false;
    this.feedbackEl.classList.remove('webinar-empty__feedback--success', 'webinar-empty__feedback--error');
    this.feedbackEl.classList.add(
      kind === 'success' ? 'webinar-empty__feedback--success' : 'webinar-empty__feedback--error'
    );
  }

  private resetFeedback(): void {
    if (!this.feedbackEl) return;
    this.feedbackEl.hidden = true;
    this.feedbackEl.textContent = '';
  }
}

export function initWebinarWaitlistForm(): void {
  const form = document.getElementById('webinarWaitlistForm') as HTMLFormElement | null;
  if (form) new WebinarWaitlistForm(form);
}

export default WebinarWaitlistForm;
